package com.yendo.mx.ui.profile

import androidx.compose.animation.*
import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.foundation.isSystemInDarkTheme
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.items
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.automirrored.filled.ArrowBack
import androidx.compose.material.icons.filled.Favorite
import androidx.compose.material.icons.filled.Star
import androidx.compose.material3.*
import androidx.compose.material3.TabRowDefaults.tabIndicatorOffset
import androidx.compose.runtime.*
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.layout.ContentScale
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import coil3.compose.AsyncImage
import com.yendo.mx.models.RestauranteFirebase
import com.yendo.mx.models.Producto
import com.yendo.mx.repository.FavoritosRepository
import com.yendo.mx.ui.theme.RosaYendo
import com.yendo.mx.utils.BackHandler
import dev.gitlive.firebase.Firebase
import dev.gitlive.firebase.firestore.firestore
import kotlinx.coroutines.launch

@OptIn(ExperimentalMaterial3Api::class)
@Composable
fun MisFavoritosScreen(
    onBack: () -> Unit,
    onRestauranteClick: (RestauranteFirebase) -> Unit,
    onProductoClick: (Producto, RestauranteFirebase) -> Unit
) {
    val favoritos by FavoritosRepository.favoritos.collectAsState()
    
    val isDark = isSystemInDarkTheme()
    val colorFondo = if (isDark) Color(0xFF121212) else Color(0xFFF8F8F8)
    val colorTextoPrincipal = if (isDark) Color.White else Color.Black
    val colorTextoSecundario = if (isDark) Color(0xFFAAAAAA) else Color.Gray

    var tabSeleccionada by remember { mutableStateOf(0) }
    val titulosTabs = listOf("Restaurantes", "Platillos")

    BackHandler { onBack() }

    Scaffold(
        topBar = {
            TopAppBar(
                title = { Text("Mis Favoritos", fontWeight = FontWeight.ExtraBold, color = colorTextoPrincipal) },
                navigationIcon = {
                    IconButton(onClick = onBack) {
                        Icon(Icons.AutoMirrored.Filled.ArrowBack, contentDescription = "Atrás", tint = colorTextoPrincipal)
                    }
                },
                colors = TopAppBarDefaults.topAppBarColors(containerColor = colorFondo)
            )
        },
        containerColor = colorFondo
    ) { padding ->
        Column(modifier = Modifier.fillMaxSize().padding(padding)) {
            TabRow(
                selectedTabIndex = tabSeleccionada,
                containerColor = colorFondo,
                contentColor = RosaYendo,
                indicator = { tabPositions ->
                    if (tabSeleccionada < tabPositions.size) {
                        TabRowDefaults.SecondaryIndicator(
                            Modifier.tabIndicatorOffset(tabPositions[tabSeleccionada]),
                            color = RosaYendo
                        )
                    }
                }
            ) {
                titulosTabs.forEachIndexed { index, title ->
                    Tab(
                        selected = tabSeleccionada == index,
                        onClick = { tabSeleccionada = index },
                        text = { 
                            Text(
                                title, 
                                fontWeight = if (tabSeleccionada == index) FontWeight.Bold else FontWeight.Normal,
                                color = if (tabSeleccionada == index) RosaYendo else colorTextoSecundario
                            ) 
                        }
                    )
                }
            }

            Box(modifier = Modifier.fillMaxSize()) {
                val favoritosFiltrados = remember(favoritos, tabSeleccionada) {
                    if (tabSeleccionada == 0) favoritos.filter { it.tipo == "RESTAURANTE" }
                    else favoritos.filter { it.tipo == "PRODUCTO" }
                }

                if (favoritosFiltrados.isEmpty()) {
                    EstadoVacioFavoritos(tabSeleccionada, colorTextoPrincipal, colorTextoSecundario)
                } else {
                    LazyColumn(
                        modifier = Modifier.fillMaxSize(),
                        contentPadding = PaddingValues(16.dp),
                        verticalArrangement = Arrangement.spacedBy(12.dp)
                    ) {
                        items(favoritosFiltrados, key = { it.id }) { fav ->
                            if (tabSeleccionada == 0) {
                                FilaFavoritoRestaurante(fav.id, onRestauranteClick)
                            } else {
                                FilaFavoritoProducto(fav.id, fav.idRestauranteAsociado, onProductoClick)
                            }
                        }
                    }
                }
            }
        }
    }
}

@Composable
fun FilaFavoritoRestaurante(id: String, onClick: (RestauranteFirebase) -> Unit) {
    var restaurante by remember { mutableStateOf<RestauranteFirebase?>(null) }
    var cargando by remember { mutableStateOf(true) }

    LaunchedEffect(id) {
        try {
            val doc = Firebase.firestore.collection("Negocios").document(id).get()
            if (doc.exists) {
                restaurante = doc.data<RestauranteFirebase>().copy(id = doc.id)
            }
        } catch (e: Exception) {
            println("YENDO_LOG ❌ [FAV] Error restaurante $id: ${e.message}")
        } finally {
            cargando = false
        }
    }

    val r = restaurante
    if (!cargando && r != null) {
        CardFavoritoHorizontal(
            titulo = r.infoPublica.nombre,
            subtitulo = r.infoPublica.categoria,
            imagenUrl = r.infoPublica.logotipo,
            rating = r.reputacion.calificacionPromedio,
            onClick = { onClick(r) },
            id = id,
            tipo = "RESTAURANTE"
        )
    }
}

@Composable
fun FilaFavoritoProducto(id: String, idRestaurante: String, onClick: (Producto, RestauranteFirebase) -> Unit) {
    var producto by remember { mutableStateOf<Producto?>(null) }
    var restaurante by remember { mutableStateOf<RestauranteFirebase?>(null) }
    var cargando by remember { mutableStateOf(true) }

    LaunchedEffect(id, idRestaurante) {
        if (idRestaurante.isBlank()) {
            println("YENDO_LOG ⚠️ [FAV] idRestaurante vacío para producto $id")
            cargando = false
            return@LaunchedEffect
        }
        try {
            println("YENDO_LOG 📦 [FAV] Cargando info para producto: $id de restaurante: $idRestaurante")
            
            // 🚀 CORRECCIÓN: Los productos están en la colección RAÍZ "Productos"
            val prodDoc = Firebase.firestore.collection("Productos").document(id).get()
            
            if (prodDoc.exists) {
                val productoData = prodDoc.data<Producto>().copy(id = prodDoc.id)
                producto = productoData
                println("YENDO_LOG ✅ [FAV] Producto cargado: ${productoData.nombre}")
                
                // Cargamos el restaurante para el subtítulo
                val restDoc = Firebase.firestore.collection("Negocios").document(idRestaurante).get()
                if (restDoc.exists) {
                    restaurante = restDoc.data<RestauranteFirebase>().copy(id = restDoc.id)
                }
            } else {
                println("YENDO_LOG ❌ [FAV] El producto $id no existe en la colección raíz.")
            }
        } catch (e: Exception) {
            println("YENDO_LOG ❌ [FAV] Error fatal cargando producto $id: ${e.message}")
        } finally {
            cargando = false
        }
    }

    val p = producto
    val r = restaurante
    if (!cargando && p != null && r != null) {
        CardFavoritoHorizontal(
            titulo = p.nombre,
            subtitulo = r.infoPublica.nombre,
            imagenUrl = p.foto,
            rating = 0.0,
            onClick = { onClick(p, r) },
            id = id,
            tipo = "PRODUCTO",
            idRestauranteAsociado = idRestaurante
        )
    }
}

@Composable
fun CardFavoritoHorizontal(
    titulo: String,
    subtitulo: String,
    imagenUrl: String,
    rating: Double,
    id: String,
    tipo: String,
    idRestauranteAsociado: String = "",
    onClick: () -> Unit
) {
    val isDark = isSystemInDarkTheme()
    val colorFondoCard = if (isDark) Color(0xFF1E1E1E) else Color.White
    val colorTextoPrincipal = if (isDark) Color.White else Color.Black
    val colorTextoSec = if (isDark) Color.LightGray else Color.Gray

    Card(
        modifier = Modifier.fillMaxWidth().clickable { onClick() },
        shape = RoundedCornerShape(16.dp),
        colors = CardDefaults.cardColors(containerColor = colorFondoCard),
        elevation = CardDefaults.cardElevation(2.dp)
    ) {
        Row(
            modifier = Modifier.padding(12.dp),
            verticalAlignment = Alignment.CenterVertically
        ) {
            AsyncImage(
                model = imagenUrl,
                contentDescription = titulo,
                contentScale = ContentScale.Crop,
                modifier = Modifier.size(70.dp).clip(RoundedCornerShape(12.dp)).background(Color.Gray.copy(0.1f))
            )
            
            Spacer(Modifier.width(16.dp))
            
            Column(modifier = Modifier.weight(1f)) {
                Text(titulo, fontWeight = FontWeight.Bold, fontSize = 16.sp, color = colorTextoPrincipal, maxLines = 1, overflow = TextOverflow.Ellipsis)
                Text(subtitulo, color = colorTextoSec, fontSize = 13.sp, maxLines = 1, overflow = TextOverflow.Ellipsis)
                if (rating > 0) {
                    Row(verticalAlignment = Alignment.CenterVertically, modifier = Modifier.padding(top = 4.dp)) {
                        Icon(Icons.Default.Star, null, tint = Color(0xFFFFB300), modifier = Modifier.size(14.dp))
                        Spacer(Modifier.width(4.dp))
                        Text(rating.toString(), fontSize = 12.sp, fontWeight = FontWeight.Bold, color = colorTextoPrincipal)
                    }
                }
            }
            
            com.yendo.mx.ui.components.BotonFavorito(
                id = id,
                tipo = tipo,
                idRestauranteAsociado = idRestauranteAsociado,
                containerColor = Color.Transparent
            )
        }
    }
}

@Composable
fun EstadoVacioFavoritos(tabIndex: Int, colorTexto: Color, colorTextoSec: Color) {
    Column(
        modifier = Modifier.fillMaxSize().padding(32.dp),
        horizontalAlignment = Alignment.CenterHorizontally,
        verticalArrangement = Arrangement.Center
    ) {
        Icon(
            Icons.Default.Favorite, 
            null, 
            tint = Color.LightGray.copy(alpha = 0.5f),
            modifier = Modifier.size(80.dp)
        )
        Spacer(Modifier.height(16.dp))
        Text(
            if (tabIndex == 0) "No tienes restaurantes favoritos" else "No tienes platillos favoritos",
            fontWeight = FontWeight.Bold,
            fontSize = 18.sp,
            color = colorTexto,
            textAlign = TextAlign.Center
        )
        Text(
            "Toca el corazón en tus lugares preferidos para guardarlos aquí.",
            fontSize = 14.sp,
            color = colorTextoSec,
            textAlign = TextAlign.Center,
            modifier = Modifier.padding(top = 8.dp)
        )
    }
}
